import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Users, Building2, CheckCircle, XCircle, UserPlus, Mail, KeyRound } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';

interface TeamInvitationData {
  id: string;
  team_id: string;
  invited_email: string;
  invited_by: string;
  status: string;
  expires_at: string;
  team: {
    name: string;
    team_code: string;
    organization_id: string;
  };
  inviter_profile: {
    first_name: string | null;
    last_name: string | null;
  } | null;
  organization: {
    name: string;
    color: string | null;
    logo_url: string | null;
  } | null;
}

interface CustomField {
  id: string;
  field_name: string;
  field_type: string;
  is_required: boolean;
  placeholder: string | null;
}

const TeamInvitation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const invitationId = searchParams.get('id');
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invitation, setInvitation] = useState<TeamInvitationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'loading' | 'signup' | 'verify' | 'profile' | 'success' | 'error'>('loading');
  const [otpCode, setOtpCode] = useState('');
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  
  
  // Profile form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address: ''
  });
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});

  // Fetch invitation data via edge function (bypasses RLS for public access)
  useEffect(() => {
    const fetchInvitation = async () => {
      if (!invitationId) {
        setError('Keine Einladungs-ID gefunden');
        setStep('error');
        setLoading(false);
        return;
      }

      try {
        // Use edge function to fetch all invitation data (bypasses RLS)
        const { data, error: fetchError } = await supabase.functions.invoke('get-invitation-details', {
          body: { invitationId }
        });

        if (fetchError) {
          console.error('Error fetching invitation:', fetchError);
          setError('Fehler beim Laden der Einladung');
          setStep('error');
          setLoading(false);
          return;
        }

        if (data?.error) {
          // Handle specific error codes
          if (data.code === 'NOT_FOUND') {
            setError('Einladung nicht gefunden');
          } else if (data.code === 'EXPIRED') {
            setError('Diese Einladung ist abgelaufen');
          } else if (data.code === 'ACCEPTED') {
            setError('Diese Einladung wurde bereits angenommen');
          } else if (data.code === 'DECLINED') {
            setError('Diese Einladung wurde bereits abgelehnt');
          } else {
            setError(data.error);
          }
          setStep('error');
          setLoading(false);
          return;
        }

        setCustomFields(data.customFields || []);
        
        setInvitation({
          id: data.invitation.id,
          team_id: data.invitation.team_id,
          invited_email: data.invitation.invited_email,
          invited_by: '',
          status: data.invitation.status,
          expires_at: data.invitation.expires_at,
          team: data.team,
          inviter_profile: data.inviterProfile,
          organization: data.organization
        });

        // Determine next step based on auth state
        if (authLoading) {
          setStep('loading');
        } else if (!user) {
          setStep('signup');
        } else {
          // Pre-fill form with existing profile data
          const { data: profileData } = await supabase
            .from('profiles')
            .select('first_name, last_name, phone, address')
            .eq('user_id', user.id)
            .single();

          if (profileData) {
            setFormData(prev => ({
              ...prev,
              first_name: profileData.first_name || '',
              last_name: profileData.last_name || '',
              phone: profileData.phone || '',
              address: profileData.address || ''
            }));
          }
          setStep('profile');
        }
      } catch (err) {
        console.error('Error fetching invitation:', err);
        setError('Fehler beim Laden der Einladung');
        setStep('error');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [invitationId, user, authLoading]);

  // Handle auth state changes
  useEffect(() => {
    if (!authLoading && invitation) {
      // Don't override verify step - user is in verification flow
      if (step === 'verify') return;
      
      if (!user) {
        setStep('signup');
      } else if (step === 'signup') {
        setStep('profile');
      }
    }
  }, [user, authLoading, invitation, step]);

  const handleSignup = async () => {
    if (!invitation) return;

    // Validate required fields
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error('Bitte geben Sie Vor- und Nachnamen ein');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Bitte geben Sie eine Telefonnummer ein');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Bitte geben Sie Ihre Adresse ein');
      return;
    }
    // Generate a random password (user will use magic link to login)
    const randomPassword = crypto.randomUUID() + crypto.randomUUID();

    // Validate custom required fields
    for (const field of customFields) {
      if (field.is_required && !customFieldValues[field.id]?.trim()) {
        toast.error(`Bitte füllen Sie das Feld "${field.field_name}" aus`);
        return;
      }
    }

    setProcessing(true);
    try {
      // Create user account with random password
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: invitation.invited_email,
        password: randomPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/team-invitation?id=${invitationId}`,
          data: {
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            phone: formData.phone.trim(),
            address: formData.address.trim()
          }
        }
      });

      if (authError) {
        if (authError.message.includes('already registered')) {
          toast.error('Diese E-Mail-Adresse ist bereits registriert. Bitte melden Sie sich an.');
        } else {
          toast.error('Fehler bei der Registrierung: ' + authError.message);
        }
        setProcessing(false);
        return;
      }

      if (!authData.user) {
        toast.error('Fehler bei der Registrierung');
        setProcessing(false);
        return;
      }

      // Store user ID and form data for after verification
      setPendingUserId(authData.user.id);
      
      // Move to verification step
      toast.success('Bestätigungs-E-Mail gesendet! Bitte prüfen Sie Ihr Postfach.');
      setStep('verify');

    } catch (err) {
      console.error('Signup error:', err);
      toast.error('Fehler bei der Registrierung');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!invitation || !pendingUserId || otpCode.length !== 6) {
      toast.error('Bitte geben Sie den 6-stelligen Code ein');
      return;
    }

    setProcessing(true);
    try {
      // Verify the OTP code
      const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
        email: invitation.invited_email,
        token: otpCode,
        type: 'signup'
      });

      if (verifyError) {
        toast.error('Ungültiger Code. Bitte versuchen Sie es erneut.');
        setProcessing(false);
        return;
      }

      if (!verifyData.user) {
        toast.error('Verifizierung fehlgeschlagen');
        setProcessing(false);
        return;
      }

      // Wait a moment for the profile trigger to create the profile
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Upsert profile - create if not exists, update if exists
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          user_id: verifyData.user.id,
          email: invitation.invited_email,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          phone: formData.phone.trim(),
          address: formData.address.trim(),
          team_id: invitation.team_id,
          organization_id: invitation.team.organization_id,
          onboarding_completed: true
        }, { onConflict: 'user_id' });

      if (profileError) {
        console.error('Profile upsert error:', profileError);
        toast.error('Profil konnte nicht erstellt werden');
        setProcessing(false);
        return;
      }

      // Save custom field values
      for (const field of customFields) {
        const value = customFieldValues[field.id] || '';
        if (value.trim()) {
          await supabase
            .from('team_member_custom_field_values')
            .upsert({
              field_id: field.id,
              user_id: verifyData.user.id,
              value: value.trim()
            }, { onConflict: 'field_id,user_id' });
        }
      }

      // Assign team member role
      await supabase
        .from('team_member_roles')
        .insert({
          team_id: invitation.team_id,
          user_id: verifyData.user.id,
          role: 'member'
        });

      // Mark invitation as accepted
      await supabase
        .from('team_invitations')
        .update({ 
          status: 'accepted',
          updated_at: new Date().toISOString()
        })
        .eq('id', invitation.id);

      toast.success('Willkommen im Team!');
      
      // Wait a moment for profile update to propagate, then redirect
      setTimeout(() => {
        window.location.href = '/';
      }, 500);

    } catch (err) {
      console.error('Verification error:', err);
      toast.error('Fehler bei der Verifizierung');
    } finally {
      setProcessing(false);
    }
  };

  const handleResendCode = async () => {
    if (!invitation) return;
    
    setProcessing(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: invitation.invited_email
      });

      if (error) {
        toast.error('Fehler beim erneuten Senden: ' + error.message);
      } else {
        toast.success('Neuer Code wurde gesendet!');
      }
    } catch (err) {
      console.error('Resend error:', err);
      toast.error('Fehler beim erneuten Senden');
    } finally {
      setProcessing(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!user || !invitation) return;

    // Validate required fields
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.phone.trim()) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Validate custom required fields
    for (const field of customFields) {
      if (field.is_required && !customFieldValues[field.id]?.trim()) {
        toast.error(`Bitte füllen Sie das Feld "${field.field_name}" aus`);
        return;
      }
    }

    setProcessing(true);
    try {
      // Update user profile with team AND organization
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          phone: formData.phone.trim(),
          address: formData.address.trim() || null,
          team_id: invitation.team_id,
          organization_id: invitation.team.organization_id,
          onboarding_completed: true
        })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      // Save custom field values
      for (const field of customFields) {
        const value = customFieldValues[field.id] || '';
        if (value.trim()) {
          await supabase
            .from('team_member_custom_field_values')
            .upsert({
              field_id: field.id,
              user_id: user.id,
              value: value.trim()
            }, { onConflict: 'field_id,user_id' });
        }
      }

      // Add default member role
      await supabase
        .from('team_member_roles')
        .insert({
          user_id: user.id,
          team_id: invitation.team_id,
          role: 'member'
        });

      // Update invitation status
      const { error: inviteError } = await supabase
        .from('team_invitations')
        .update({ 
          status: 'accepted',
          updated_at: new Date().toISOString()
        })
        .eq('id', invitation.id);

      if (inviteError) throw inviteError;

      setStep('success');
      toast.success('Willkommen im Team!');

      // Redirect to home after a short delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      toast.error('Fehler beim Annehmen der Einladung');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!invitation) return;
    
    setProcessing(true);
    try {
      await supabase
        .from('team_invitations')
        .update({ 
          status: 'declined',
          updated_at: new Date().toISOString()
        })
        .eq('id', invitation.id);

      toast.success('Einladung abgelehnt');
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Error declining invitation:', err);
      toast.error('Fehler beim Ablehnen der Einladung');
    } finally {
      setProcessing(false);
    }
  };

  const orgColor = invitation?.organization?.color || '#dc2626';

  if (step === 'loading' || loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <div className="mx-auto h-16 w-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Einladung ungültig</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/')}>Zur Startseite</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <div className="mx-auto h-16 w-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">E-Mail gesendet!</h2>
            <p className="text-muted-foreground mb-6">
              Wir haben einen Login-Link an <strong>{invitation?.invited_email}</strong> gesendet. 
              Klicken Sie auf den Link in der E-Mail, um Ihr Konto zu aktivieren und dem Team beizutreten.
            </p>
            <p className="text-sm text-muted-foreground">
              Bitte prüfen Sie auch Ihren Spam-Ordner.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div 
              className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: `${orgColor}20` }}
            >
              <KeyRound className="h-8 w-8" style={{ color: orgColor }} />
            </div>
            <CardTitle>E-Mail bestätigen</CardTitle>
            <CardDescription>
              Wir haben einen Bestätigungscode an <strong>{invitation?.invited_email}</strong> gesendet.
              Bitte geben Sie den 6-stelligen Code ein.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={otpCode}
                onChange={(value) => setOtpCode(value)}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <p className="text-sm text-muted-foreground text-center">
              Bitte prüfen Sie auch Ihren Spam-Ordner.
            </p>

            <div className="space-y-3">
              <Button 
                onClick={handleVerifyOtp}
                disabled={processing || otpCode.length !== 6}
                className="w-full"
                style={{ backgroundColor: orgColor }}
              >
                {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <CheckCircle className="h-4 w-4 mr-2" />
                Bestätigen
              </Button>
              
              <Button 
                variant="outline"
                onClick={handleResendCode}
                disabled={processing}
                className="w-full"
              >
                Code erneut senden
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'signup') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div 
              className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: `${orgColor}20` }}
            >
              {invitation?.organization?.logo_url ? (
                <img 
                  src={invitation.organization.logo_url} 
                  alt={invitation.organization.name}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <Building2 className="h-8 w-8" style={{ color: orgColor }} />
              )}
            </div>
            <CardTitle>Konto erstellen</CardTitle>
            <CardDescription>
              {invitation?.inviter_profile?.first_name} {invitation?.inviter_profile?.last_name} lädt Sie ein, dem Team "{invitation?.team?.name}" beizutreten
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Team info */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <Users className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">{invitation?.team?.name}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Organisation: {invitation?.organization?.name}
              </p>
            </div>

            {/* Email (read-only, from invitation) */}
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={invitation?.invited_email || ''}
                  disabled
                  className="pl-10 bg-muted"
                />
              </div>
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Vorname *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Max"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Nachname *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Mustermann"
                  required
                />
              </div>
            </div>
            
            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">Telefonnummer *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+49 123 456789"
                required
              />
            </div>
            
            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">Adresse *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Musterstraße 1, 12345 Stadt"
                required
              />
            </div>


            {/* Custom team fields */}
            {customFields.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                <Label className="text-xs text-muted-foreground">Team-spezifische Felder</Label>
                {customFields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <Label htmlFor={`custom-${field.id}`}>
                      {field.field_name} {field.is_required && '*'}
                    </Label>
                    <Input
                      id={`custom-${field.id}`}
                      type={field.field_type === 'number' ? 'number' : field.field_type === 'email' ? 'email' : 'text'}
                      value={customFieldValues[field.id] || ''}
                      onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: e.target.value })}
                      placeholder={field.placeholder || `${field.field_name} eingeben...`}
                      required={field.is_required}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={handleDecline}
                disabled={processing}
                className="flex-1"
              >
                Ablehnen
              </Button>
              <Button 
                onClick={handleSignup}
                disabled={processing}
                className="flex-1"
                style={{ backgroundColor: orgColor }}
              >
                {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <UserPlus className="h-4 w-4 mr-2" />
                Beitreten
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Profile step (for already logged in users)
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div 
            className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4"
            style={{ backgroundColor: `${orgColor}20` }}
          >
            {invitation?.organization?.logo_url ? (
              <img 
                src={invitation.organization.logo_url} 
                alt={invitation.organization.name}
                className="h-8 w-8 object-contain"
              />
            ) : (
              <Building2 className="h-8 w-8" style={{ color: orgColor }} />
            )}
          </div>
          <CardTitle>Willkommen bei {invitation?.team?.name}!</CardTitle>
          <CardDescription>
            {invitation?.inviter_profile?.first_name} {invitation?.inviter_profile?.last_name} hat Sie eingeladen.
            Bitte vervollständigen Sie Ihr Profil.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Standard fields */}
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Vorname *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Max"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Nachname *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Mustermann"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Telefonnummer *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+49 123 456789"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">Adresse *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Musterstraße 1, 12345 Stadt"
                required
              />
            </div>
          </div>

          {/* Custom team fields */}
          {customFields.length > 0 && (
            <div className="space-y-4 pt-4 border-t">
              <Label className="text-xs text-muted-foreground">Team-spezifische Felder</Label>
              {customFields.map((field) => (
                <div key={field.id} className="space-y-2">
                  <Label htmlFor={`custom-${field.id}`}>
                    {field.field_name} {field.is_required && '*'}
                  </Label>
                  <Input
                    id={`custom-${field.id}`}
                    type={field.field_type === 'number' ? 'number' : field.field_type === 'email' ? 'email' : 'text'}
                    value={customFieldValues[field.id] || ''}
                    onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: e.target.value })}
                    placeholder={field.placeholder || `${field.field_name} eingeben...`}
                    required={field.is_required}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button 
              variant="outline" 
              onClick={handleDecline}
              disabled={processing}
              className="flex-1"
            >
              Ablehnen
            </Button>
            <Button 
              onClick={handleAcceptInvitation}
              disabled={processing}
              className="flex-1"
              style={{ backgroundColor: orgColor }}
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Beitreten
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamInvitation;
